import {Inject, Injectable} from '@angular/core';
import {EmployeeEditHistoryEntry, TimecardRow, Timecard} from 'resteasytox';
import {Employee, } from 'resteasytox';
import {Moment} from 'moment';
import * as moment from 'moment-timezone';
import {
	EmployeeEditHistoryDetails,
	EmployeesEditHistoryCacheService
} from '../core/employee-edit-history-cache.service';

import * as _ from 'lodash';
import {EmployeeType} from 'app/settings/settings-employees/settings-employees.component';
import {
	HOLIDAYS_WEEKENDS_CHECK_SERVICE,
	HolidaysWeekendsCheckService
} from '../shared/upgrade/holidays-weekends-check-service.service';

export type PaymentFrequency = 'bi-weekly' | 'monthly' | 'no-timecard';

export const PAYMENT_FREQUENCY: {[key: string]: PaymentFrequency} = {
	BIWEEKLY: 'bi-weekly',
	MONTHLY: 'monthly',
	NO_TIMECARD: 'no-timecard'
};

export type TimecardPeriod = string[];

export interface BillableUnitsData {
	value: number;
	overridden: boolean;
}

export class TtTimecard extends Timecard {
	employeeId: number;
	startDate: Date;
	endDate: Date;
}

export const VIEW_DATE_FORMAT: string = 'MM/DD/YYYY';

@Injectable()
export class TimecardBuilderService {

	private timecardRanges: any = {};
	private years: number[] = [];

	constructor(
		private employeeEditHistoryCacheService: EmployeesEditHistoryCacheService,
		@Inject(HOLIDAYS_WEEKENDS_CHECK_SERVICE) private holidaysWeekendsCheckServiceNg1: HolidaysWeekendsCheckService,
	) {
		this.years = this.generateYears(1999);
		this.timecardRanges[PAYMENT_FREQUENCY.BIWEEKLY] = this.generateDateRanges(this.years.slice().reverse(), PAYMENT_FREQUENCY.BIWEEKLY);
		this.timecardRanges[PAYMENT_FREQUENCY.MONTHLY] = this.generateDateRanges(this.years.slice().reverse(), PAYMENT_FREQUENCY.MONTHLY);
	}

	public getYears(to?: number): number[] {
		if (!to) { return this.years; }

		return _.filter(this.years, (year: number) => {
			return year <= to;
		});
	}

	public getYearPeriods(paymentFrequency: PaymentFrequency, year: number): TimecardPeriod[] {
		if (this.timecardRanges[paymentFrequency] && this.timecardRanges[paymentFrequency][year]) {
			return this.timecardRanges[paymentFrequency][year];
		}
		return [];
	}


	public buildEmployeeTimecardsForPeriod(dates: any, employee: Employee, overridenTimecardRows: {[date: string]: TimecardRow}, submittedTimecards: Timecard[], daysOff: any[], nonDownloaded: boolean): any[] {
		const timecards: any[] = [];
		let timecard: any;
		let timecardPeriod: TimecardPeriod;
		let employeeDetails: EmployeeEditHistoryDetails;
		let periodS: Moment, periodE: Moment;
		let formattedDate: string;

		const sortedDates: string[] = _.sortBy(_.keys(dates));
		let currentDate: Moment = this.tzm(sortedDates[0], VIEW_DATE_FORMAT);
		const endDate: Moment = this.tzm(_.last(sortedDates), VIEW_DATE_FORMAT);

		while (currentDate.isSameOrBefore(endDate)) {
			timecard = {};
			employeeDetails = this.employeeEditHistoryCacheService.getEmployeeDetailsAtDate(employee.id, currentDate.valueOf());
			if (!employeeDetails) { currentDate.add(1, 'day'); continue; }
			timecardPeriod = this.getTimecardPeriodByDate(currentDate.format(VIEW_DATE_FORMAT), employeeDetails.payFrequency as PaymentFrequency);
			if (!timecardPeriod) { currentDate.add(1, 'day'); continue; }
			periodS = this.tzm(timecardPeriod[0], VIEW_DATE_FORMAT);
			periodE = this.tzm(timecardPeriod[1], VIEW_DATE_FORMAT);

			// Build timecard rows
			const rows: any[] = [];
			while (periodS.isSameOrBefore(periodE)) {
				formattedDate = periodS.format(VIEW_DATE_FORMAT);
				if (dates[formattedDate]) {
					rows.push(this.fillTimecardRow(formattedDate, dates[formattedDate], overridenTimecardRows[formattedDate], daysOff, employeeDetails.employeeType));
				}
				periodS.add(1, 'day');
			}

			const submittedTimecard: Timecard = _.find(submittedTimecards, (sT: Timecard) => {
				return (sT as any)['employeeId'] === employee.id
					&& sT['startDate'] === timecardPeriod[0]
					&& sT['endDate'] === timecardPeriod[1];
			});
			// Add employee data
			timecard = {
				emplID: employee.id,
				firstName: employee.firstName,
				lastName: employee.lastName,
				nonDownloaded: nonDownloaded,
				employeeType: employeeDetails.employeeType,
				paymentFrequency: employeeDetails.payFrequency,
				// payType: employeeDetails.payType,
				dates: rows
			};

			if (submittedTimecard && submittedTimecard.transactionId) {
				timecard['submitted'] = true;
				timecard['transactionId'] = submittedTimecard.transactionId;
				timecard['submittedDate'] = submittedTimecard.createdDate;
			}

			if (submittedTimecard && submittedTimecard.waitApproval) {
				timecard['timecardId'] = submittedTimecard.id;
				timecard['waitApproval'] = true;
			}

			// timecard['sequenceId'] = this.getTimecardNumberByDate(currentDate.format(VIEW_DATE_FORMAT), this.employeeEditHistoryCacheService.getEmployeeEditHistory(employee.id), employee);
			timecard['sequenceId'] = this.getTimecardSequenceId(employee, timecardPeriod);

			timecards.push(timecard);

			currentDate = periodE.clone().add(1, 'day');
		}


		return timecards;
	}

	private fillTimecardRow(date: string, timecardRow: TimecardRow, overriddenRow: TimecardRow, daysOff: any[], employeeType: EmployeeType): any {

		// const ttRow: any = {};
		const billableUnitsData: BillableUnitsData = this.calculateBillableUnitsForDate(date, timecardRow, overriddenRow, daysOff, employeeType);
		return {
			overridden: billableUnitsData.overridden,
			billingUnits: this.formatTimecardTableValue(billableUnitsData.value.toString()),
			billable: this.formatTimecardTableValue(timecardRow['billable']),
			non_billable: this.formatTimecardTableValue(timecardRow['non_billable']),
			ah_hours: this.formatTimecardTableValue(timecardRow['ah_hours']),
			date: new Date(date),
			nonDownloaded: timecardRow['nonDownloaded'],
			note: (overriddenRow) ? overriddenRow.note : undefined
		};
	}

	public getTimecardNumberByDate(date: string, employeeEditHistory: EmployeeEditHistoryEntry[], employee: Employee): number {
		const d: Moment = this.tzm(date);
		const startWork: Moment = this.tzm(employee.startWork);

		if (d.isBefore(startWork)) { return null; }

		if (employee.finishWork) {
			const endWork: Moment = this.tzm(employee.finishWork);
			if (d.isAfter(endWork)) { return null; }
		}

		const employeeDetails: EmployeeEditHistoryDetails = this.employeeEditHistoryCacheService.getEmployeeDetailsAtDate(employee.id, d.valueOf());
		const formattedDate: string = d.format(VIEW_DATE_FORMAT);
		const timecardPeriod: TimecardPeriod = this.getTimecardPeriodByDate(formattedDate, employeeDetails.payFrequency as PaymentFrequency);

		if (!timecardPeriod) {
			console.log('Date is out of range');
			return null;
		}

		return this.calculateTimecardNumber(employee.id, timecardPeriod);
	}

	public getTimecardPeriodByDate(date: string, paymentFrequency: PaymentFrequency): TimecardPeriod {
		// if (paymentFrequency === PAYMENT_FREQUENCY.NO_TIMECARD) { return null; }
		if (paymentFrequency === PAYMENT_FREQUENCY.NO_TIMECARD) {
			paymentFrequency = PAYMENT_FREQUENCY.MONTHLY;
		}
		const d: Moment = this.tzm(date, VIEW_DATE_FORMAT);
		let result: TimecardPeriod = null;
		let temp: TimecardPeriod;

		_.each([d.year() - 1, d.year(), d.year() + 1], (year: number) => {
			temp = _.find(this.timecardRanges[paymentFrequency][year], (range: TimecardPeriod) => {
				return d.isSameOrAfter(this.tzm(range[0], VIEW_DATE_FORMAT)) && d.isSameOrBefore(this.tzm(range[1], VIEW_DATE_FORMAT));
			});
			if (temp) {
				result = temp;
				return false;
			}
		});

		return result;
	}

	public calculateBillableUnitsForDate(date: string, timecardRow: any, overriddenRow: TimecardRow, daysOff: any[], employeeType: EmployeeType): any {
		// employeeType === 1 => HOURLY_1099
		// employeeType === 3 => HOURLY_W2
		// if employee paid daily and worked after hours, increase billing units value
		let value: number = (employeeType === EmployeeType.HOURLY_1099 || employeeType === EmployeeType.HOURLY_W2) ? 0 : (1 * 8 + parseFloat(timecardRow['ah_hours'])) / 8;
		const today: Date = new Date();

		// const overriddenRow: any = this.getOverriddenRow(date, emplId);

		if (overriddenRow && overriddenRow.billingUnits !== null) {
			return { value: overriddenRow.billingUnits, overridden: true };
		}

		if (employeeType === EmployeeType.HOURLY_1099 || employeeType === EmployeeType.HOURLY_W2) {
			return { value: parseFloat(timecardRow.billable) + parseFloat(timecardRow.non_billable) / 8, overridden: false };
		}

		const itemDate: Date = new Date(date);
		// const daysOff: any[] = this.daysOff[emplId];
		if (itemDate.getTime() > today.getTime() || (parseFloat(timecardRow.billable) === 0 && parseFloat(timecardRow.non_billable) === 0)) {
			return {value: 0, overridden: false};
		}
		if ((this.holidaysWeekendsCheckServiceNg1.isWeekend(date) || this.holidaysWeekendsCheckServiceNg1.isHoliday(date)) || parseFloat(timecardRow.billable) > 8) {
			return {value: parseFloat(timecardRow.billable) / 8, overridden: false };
		}

		_.each(daysOff, (dayOff: any) => {
			if (dayOff.date === date) {
				value -= dayOff.hours / 8;
			}
		});

		return {value: value, overridden: false};
	}

	public calculateBillableUnitsForDateEmployeeId(date: string, timecardRow: any, overriddenRow: TimecardRow, daysOff: any[], employeeId: number): any {
		const employeeDetails: EmployeeEditHistoryDetails = this.employeeEditHistoryCacheService.getEmployeeDetailsAtDate(employeeId, this.tzm(date, VIEW_DATE_FORMAT).valueOf());
		return this.calculateBillableUnitsForDate(date, timecardRow, overriddenRow, daysOff, employeeDetails.employeeType);
	}

	public getTimecardSequenceId(employee: Employee, timecardPeriod: TimecardPeriod): string {
		const timecardNumber: number = this.calculateTimecardNumber(employee.id, timecardPeriod);
		return this.buildTimecardSequenceId(timecardNumber, employee);

	}

	public generateEmployeeTimecardsPeriodsForPeriod(from: number, to: number, employee: Employee): TimecardPeriod[] {
		const periods: TimecardPeriod[] = [];
		let timecardPeriod: TimecardPeriod;
		let employeeDetails: EmployeeEditHistoryDetails;
		let periodE: Moment;
		let currentDate: Moment = this.tzm(from);
		const endDate: Moment = this.tzm(to);

		while (currentDate.isSameOrBefore(endDate)) {
			employeeDetails = this.employeeEditHistoryCacheService.getEmployeeDetailsAtDate(employee.id, currentDate.valueOf());
			if (!employeeDetails) { currentDate.add(1, 'day'); continue; }

			timecardPeriod = this.getTimecardPeriodByDate(currentDate.format(VIEW_DATE_FORMAT), employeeDetails.payFrequency as PaymentFrequency);
			if (!timecardPeriod) { currentDate.add(1, 'day'); continue; }
			periodE = this.tzm(timecardPeriod[1], VIEW_DATE_FORMAT);

			if (employeeDetails.payFrequency !== PAYMENT_FREQUENCY.NO_TIMECARD) {
				periods.push(timecardPeriod);
			}

			currentDate = periodE.clone().add(1, 'day');
		}


		return periods;
	}

	private calculateTimecardNumber(employeeId: number, timecardPeriod: TimecardPeriod): number {
		const employeeEditHistory: EmployeeEditHistoryEntry[] = this.employeeEditHistoryCacheService.getEmployeeEditHistory(employeeId);
		// return null if no timecard for speicified period
		let timecardNumber: number = null;
		let paymentFrequency: PaymentFrequency;
		let toPeriod: number;

		_.each(employeeEditHistory, (entry: EmployeeEditHistoryEntry) => {
			if (this.tzm(timecardPeriod[1], VIEW_DATE_FORMAT).isBefore(this.tzm(entry.startDate).startOf('day'))) {
				// timecard period is before employee started to work;
				// stop further iteration
				return false;
			}

			if (this.tzm(timecardPeriod[0], VIEW_DATE_FORMAT).isBefore(this.tzm(entry.startDate)) && timecardNumber) {
				// if timecard starts at another time entry
				// then number is calculated using previous history entry details

				// timecardNumber is null if it is the first entry in employee history
				// in this case calculate timecard number as usual
				return false;
			}


			if (entry.payFrequency) {
				paymentFrequency = entry.payFrequency as PaymentFrequency;
			}

			if (paymentFrequency && paymentFrequency !== PAYMENT_FREQUENCY.NO_TIMECARD ) {
				if (!timecardNumber) {
					timecardNumber = 0;
				}

				toPeriod = entry.endDate;
				if (!entry.endDate) {
					toPeriod = this.tzm(timecardPeriod[1], VIEW_DATE_FORMAT).valueOf();
				}

				if (entry.endDate && entry.endDate >= this.tzm(timecardPeriod[1], VIEW_DATE_FORMAT).valueOf()) {
					toPeriod = this.tzm(timecardPeriod[1], VIEW_DATE_FORMAT).valueOf();
				}


				timecardNumber += this.countTimecardsInRange(entry.startDate, toPeriod, paymentFrequency);
			}
		});

		// if period is within range where employee has no timecard
		// return null
		if (paymentFrequency && paymentFrequency === PAYMENT_FREQUENCY.NO_TIMECARD) {
			return null;
		}

		// return employee.firstName.charAt(0) + employee.lastName.charAt(0) + this.addLeadingZerosForSequenceId(timecardNumber);
		return timecardNumber;
	}

	private buildTimecardSequenceId(timecardNumber: number, employee: Employee): string {
		if (!timecardNumber) { return null; }
		return `${employee.firstName[0]}${employee.lastName[0]}${this.addLeadingZerosForSequenceId(timecardNumber)}`;
	}

	private countTimecardsInRange(from: number, to: number, paymentFrequency: PaymentFrequency): number {
		if (paymentFrequency === PAYMENT_FREQUENCY.NO_TIMECARD) { return 0; }
		const f: Moment = this.tzm(from);
		const t: Moment = this.tzm(to);
		let rf: Moment, rt: Moment;
		let count: number = 0;
		let iterYear: number = f.year();

		while (iterYear <= t.year()) {
			_.each(this.timecardRanges[paymentFrequency][iterYear], (range: TimecardPeriod) => {
				rf = this.tzm(range[0]);
				rt = this.tzm(range[1]);
				if ( (rf.isSameOrAfter(f) && rf.isSameOrBefore(t)) || (rt.isSameOrAfter(f) && rt.isSameOrBefore(t)) ) {
					count++;
				}
			});

			iterYear++;
		}

		return count;
	}

	private generateYears(start: number): number[] {
		const years: number[] = [];
		const newYear: number = new Date().getFullYear() + 1;

		for (let i: number = newYear; i >= start; i--) {
			years.push(i);
		}

		return years;
	}

	public generateRangesForYear(year: number, previousYearRanges: any, paymentFrequency: PaymentFrequency, startDate: string, endDate: string): TimecardPeriod[] {
		if (paymentFrequency === PAYMENT_FREQUENCY.BIWEEKLY) {
			return this.generateBiweeklyRanges(year, previousYearRanges, startDate, endDate);
		} else {
			return this.generateMonthlyRanges(year, startDate, endDate);
		}
	};

	// change this method very carefully to don't break dates of previous timecards
	private generateBiweeklyRanges(year: number, previousYearRanges: TimecardPeriod[], startDate: string, endDate: string): TimecardPeriod[] {
		const today: Date = new Date();

		let sDate: Moment = (!previousYearRanges) ? this.tzm(year + '/1/1', 'YYYY/MM/DD') : this.tzm(previousYearRanges[previousYearRanges.length - 1][1], 'MM/DD/YYYY');
		const startWorkDate: Moment = (startDate) ? this.tzm(startDate, 'MM/DD/YYYY') : this.tzm(year + '/1/1', 'YYYY/MM/DD');
		const endWorkDate: Moment = (endDate) ? this.tzm(endDate, 'MM/DD/YYYY') : this.tzm().endOf('year');
		const sDateDay: number = sDate.day();
		let sDateDayOffset: number = 0;
		const ranges: string[][] = [];
		if (sDateDay !== 1) { // all ranges should start from the monday
			if (sDateDay > 1) {
				sDateDayOffset = ((7 - sDateDay) % 6) + 1;
			} else {
				sDateDayOffset = ((7 - sDateDay) % 6);
			}
			sDate.add(sDateDayOffset, 'days');
		}
		let fDate: Moment = this.tzm(sDate).add(13, 'days');
		do {
			if ( ( (sDate.isBefore(startWorkDate) || sDate.isSame(startWorkDate)) && (fDate.isAfter(startWorkDate) || fDate.isSame(startWorkDate)) )
				|| (sDate.isAfter(startWorkDate) && fDate.isBefore(endWorkDate))
				|| (sDate.isBefore(endWorkDate)  && ( fDate.isAfter(endWorkDate) || fDate.isSame(endWorkDate) ) )) {
				ranges.push([sDate.format('MM/DD/YYYY'), fDate.format('MM/DD/YYYY')]);
			}

			sDate = this.tzm(fDate).add(1, 'days');
			fDate = this.tzm(sDate).add(13, 'days');
		} while (sDate.year() <= year);
		// } while (sDate.year() <= year && sDate.unix() * 1000 < today.getTime());
		return ranges;
	}

	private generateMonthlyRanges(year: number, startDate: string, endDate: string): TimecardPeriod[] {
		const ranges: TimecardPeriod[] = [];
		const today: Date = new Date();
		let sDate: Moment = this.tzm(year + '/1/1', 'YYYY/MM/DD');
		let fDate: Moment = this.tzm(sDate).add(1, 'month');
		const startWorkDate: Moment = (startDate) ? this.tzm(startDate, 'MM/DD/YYYY') : this.tzm(year + '/1/1', 'YYYY/MM/DD');
		const endWorkDate: Moment = (endDate) ? this.tzm(endDate, 'MM/DD/YYYY') : this.tzm().endOf('year');
		fDate = this.tzm(fDate).subtract(1, 'day');
		do {
			if ( ( (sDate.isBefore(startWorkDate) || sDate.isSame(startWorkDate)) && (fDate.isAfter(startWorkDate) || fDate.isSame(startWorkDate)) )
				|| (sDate.isAfter(startWorkDate) && fDate.isBefore(endWorkDate))
				|| (sDate.isBefore(endWorkDate)  && ( fDate.isAfter(endWorkDate) || fDate.isSame(endWorkDate) ) )) {
				ranges.push([sDate.format('MM/DD/YYYY'), fDate.format('MM/DD/YYYY')]);
			}
			sDate = this.tzm(fDate).add(1, 'day');
			fDate = this.tzm(sDate).add(1, 'month').subtract(1, 'day');
		} while (sDate.year() <= year);
		// } while (sDate.year() <= year && sDate.unix() * 1000 < today.getTime() );
		return ranges;
	}

	// TODO: add types for params
	private generateDateRanges(years: number[], paymentFrequency: PaymentFrequency | string, startDate?: string, endDate?: string): any {
		const results: any = {};
		_.each(years, (year: number) => {
			// range generation method had a bug in range dates generation
			// but to save old already payed timecards, 'fixed' generation applied only since 2019 year
			if (year < 2019) {
				results[year] = this.generateRangesForYear(year, null, <PaymentFrequency>paymentFrequency, startDate, endDate);
			} else {
				results[year] = this.generateRangesForYear(year, results[year - 1], <PaymentFrequency>paymentFrequency, startDate, endDate);
			}
		});

		return results;
	}

	// user view and formatting mehtods

	public calculateTimecardTotalDays(timecard: any): string {
		let days: number = 0;
		_.each(timecard.dates, (date: any) => {
			days += parseFloat(date.billingUnits);
		});

		return days.toFixed(2);
	}

	public calculateTimecardTotalHours(timecard: any, type: 'billable' | 'non_billable'): string {
		let totalHours: number = 0;
		_.each(timecard.dates, (date: any) => {
			totalHours += parseFloat(date[type]);
		});

		return totalHours.toFixed(2);
	}

	private addLeadingZerosForSequenceId(value: number): string {
		if (value < 10) {
			return '000' + value;
		} else if (value < 100 ) {
			return '00' + value;
		} else if (value < 1000) {
			return '0' + value;
		}
	}

	private formatTimecardTableValue(value: string | number): string {
		return parseFloat(value.toString()).toFixed(2);
	}

	// moment constructor with NY timezone
	private tzm(value?: any, format?: string): Moment {
		if (format) {
			return moment.tz(value, format, 'America/New_York');
		} else {
			return moment.tz(value, 'America/New_York');
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/reports/timecards-builder.service.ts